

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { FiFilter, FiX, FiRefreshCcw } from 'react-icons/fi';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaCalendarAlt, FaDownload, FaSearch, FaSortUp, FaSortDown } from "react-icons/fa";
import { BsFillInfoCircleFill, BsCheckCircle, BsXCircle, BsClock } from "react-icons/bs";
import { sendGlobalWS, useWebSocket } from '../../context/UseWebSocketContext';
import { toast } from 'react-toastify';
import { Modal, Button } from 'react-bootstrap';

const AlertTableView = ({ darkMode = false }) => {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [sortOrder, setSortOrder] = useState("desc");
    const [searchText, setSearchText] = useState('');
    const [sortColumn, setSortColumn] = useState('created');
    const [currentPage, setCurrentPage] = useState(1);
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedAlert, setSelectedAlert] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const searchContainerRef = useRef(null);
    const searchInputRef = useRef(null);
    const rowsPerPage = 15;
    const loadingTimeoutRef = useRef(null);
    const initialLoadDoneRef = useRef(false);
    const isMountedRef = useRef(true);
    const autoRefreshIntervalRef = useRef(null);
    const previousAlertsLengthRef = useRef(0);

    // Get alerts directly from WebSocket
    const { alertsData } = useWebSocket();
    const alerts = Array.isArray(alertsData) ? alertsData : [];

    // Format date without seconds
    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString([], {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Apply dark mode
    useEffect(() => {
        if (darkMode) {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
    }, [darkMode]);

    // Force re-render when darkMode changes
    useEffect(() => {
        const timeout = setTimeout(() => {
            console.log("🔄 Dark mode toggled:", darkMode);
            document.body.style.display = 'none';
            const forceReflow = document.body.offsetHeight;
            document.body.style.display = '';
        }, 10);
        return () => clearTimeout(timeout);
    }, [darkMode]);

    useEffect(() => {
        setCurrentPage(1);
    }, [alerts.length, searchText]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target) && !searchText) {
                setIsSearchExpanded(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [searchText]);

    // Cleanup on unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }
            if (autoRefreshIntervalRef.current) {
                clearInterval(autoRefreshIntervalRef.current);
            }
        };
    }, []);

    // Monitor alertsData changes
    useEffect(() => {
        if (isLoading && isMountedRef.current) {
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }
            setIsLoading(false);
            setIsRefreshing(false);
            console.log("✅ Data loaded, alerts count:", alerts.length);
        }
    }, [alerts.length, isLoading]);

    // AUTO REFRESH EVERY 30 SECONDS
    useEffect(() => {
        if (!isMountedRef.current) return;
        
        if (autoRefreshIntervalRef.current) {
            clearInterval(autoRefreshIntervalRef.current);
        }
        
        autoRefreshIntervalRef.current = setInterval(() => {
            if (isMountedRef.current && !isLoading) {
                console.log("🔄 Auto-refreshing alerts...");
                const formatted = selectedDate.toISOString().split("T")[0];
                setIsRefreshing(true);
                sendGlobalWS({ r: "alerts", arg: formatted });
                
                setTimeout(() => {
                    if (isMountedRef.current) {
                        setIsRefreshing(false);
                    }
                }, 2000);
            }
        }, 30000);
        
        return () => {
            if (autoRefreshIntervalRef.current) {
                clearInterval(autoRefreshIntervalRef.current);
            }
        };
    }, [selectedDate, isLoading]);

    // Load data for selected date
    const loadAlertsForDate = useCallback((date, showToastMessage = false) => {
        const formatted = date.toISOString().split("T")[0];
        console.log("📅 Loading alerts for date:", formatted);
        
        if (!isMountedRef.current) return;
        
        setIsLoading(true);
        
        if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
        }
        
        loadingTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current && isLoading) {
                console.log("⏰ Loading timeout, stopping loader");
                setIsLoading(false);
                setIsRefreshing(false);
                if (showToastMessage) {
                    toast.warning(`Connection timeout, please try again`);
                }
            }
        }, 15000);
        
        sendGlobalWS({ r: "alerts", arg: formatted });
        
        if (showToastMessage) {
            toast.info(`Loading alerts for ${formatted}...`);
        }
    }, [isLoading]);

    // Date change handler
    const handleDateChange = (date) => {
        if (date) {
            setSelectedDate(date);
            setCurrentPage(1);
            loadAlertsForDate(date, true);
        }
    };

    // Load data for current date on component mount
    useEffect(() => {
        if (!initialLoadDoneRef.current && isMountedRef.current) {
            const today = new Date();
            setSelectedDate(today);
            initialLoadDoneRef.current = true;
            loadAlertsForDate(today, false);
        }
        
        return () => {
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }
        };
    }, []);

    // Manual refresh
    const handleRefresh = useCallback(() => {
        console.log("🔄 Manual refresh triggered");
        setIsRefreshing(true);
        loadAlertsForDate(selectedDate, true);
        setTimeout(() => {
            if (isMountedRef.current) {
                setIsRefreshing(false);
            }
        }, 2000);
    }, [selectedDate, loadAlertsForDate]);

    const handleDownload = () => {
        if (!alerts || alerts.length === 0) {
            toast.warning("No data to download!");
            return;
        }

        const headers = ['Created', 'Symbol', 'Exchange', 'Buy/Sell', 'Order Type', 'Status', 'Error Message'];
        const rows = alerts.map(row => [
            row.created,
            row.symbol,
            row.exchange,
            row.buy_sell,
            row.order_type,
            row.status,
            row.error_msg || '-'
        ]);

        const csvContent = [headers, ...rows].map(row => 
            row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
        ).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
        a.href = url;
        a.download = `alerts-data-${timestamp}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Download started!");
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortOrder("asc");
        }
    };

    const getSortIcon = (column) => {
        if (sortColumn !== column) return <FiFilter size={9} className="opacity-50" />;
        return sortOrder === "asc" ? <FaSortUp size={9} /> : <FaSortDown size={9} />;
    };

    const handleSearchExpand = () => {
        setIsSearchExpanded(true);
        setTimeout(() => {
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }, 100);
    };

    const clearSearch = () => {
        setSearchText('');
        setIsSearchExpanded(false);
        setCurrentPage(1);
    };

    const handleShowDetails = (alert) => {
        setSelectedAlert(alert);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedAlert(null);
    };

    // Filter and sort data
    let filteredData = [...alerts].filter(row => {
        const searchLower = searchText.toLowerCase();
        if (!searchLower) return true;
        return (
            row.symbol?.toLowerCase().includes(searchLower) ||
            row.exchange?.toLowerCase().includes(searchLower) ||
            row.created?.toLowerCase().includes(searchLower) ||
            row.buy_sell?.toLowerCase().includes(searchLower) ||
            row.order_type?.toLowerCase().includes(searchLower) ||
            row.status?.toLowerCase().includes(searchLower)
        );
    });

    filteredData = filteredData.filter((row, index, self) => 
        index === self.findIndex(r => r.id === row.id)
    );

    filteredData.sort((a, b) => {
        let aVal = a[sortColumn] || '';
        let bVal = b[sortColumn] || '';
        
        if (sortColumn === 'created') {
            aVal = new Date(aVal);
            bVal = new Date(bVal);
        } else {
            aVal = String(aVal).toLowerCase();
            bVal = String(bVal).toLowerCase();
        }
        
        if (sortOrder === "asc") {
            return aVal > bVal ? 1 : -1;
        } else {
            return aVal < bVal ? 1 : -1;
        }
    });

    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    const startIdx = (currentPage - 1) * rowsPerPage;
    const currentRows = filteredData.slice(startIdx, startIdx + rowsPerPage);

    const getStatusIcon = (status) => {
        const statusLower = status?.toLowerCase() || '';
        if (statusLower.includes('success') || statusLower.includes('completed')) {
            return <BsCheckCircle className="text-green-500" size={9} />;
        } else if (statusLower.includes('pending')) {
            return <BsClock className="text-yellow-500" size={9} />;
        } else if (statusLower.includes('failed') || statusLower.includes('error')) {
            return <BsXCircle className="text-red-500" size={9} />;
        }
        return null;
    };

    const getStatusColor = (status) => {
        const statusLower = status?.toLowerCase() || '';
        if (statusLower.includes('success') || statusLower.includes('completed')) {
            return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/40';
        } else if (statusLower.includes('pending')) {
            return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/40';
        } else if (statusLower.includes('failed') || statusLower.includes('error')) {
            return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/40';
        } else {
            return 'text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800';
        }
    };

    const getTextColor = () => {
        return darkMode ? 'text-gray-400' : 'text-gray-700';
    };

    // CSS classes
    const containerClass = `transition-colors duration-200 h-full flex flex-col bg-[var(--bg-primary)]`;
    const cardClass = `rounded-lg overflow-hidden transition-colors duration-200 flex flex-col h-full
        bg-[var(--bg-card)] border border-[var(--border-color)] shadow-md`;
    const buttonClass = `p-1 rounded-md transition-all duration-200 text-[var(--text-muted)] hover:bg-[var(--bg-hover)]`;
    const tableHeaderClass = `sticky top-0 z-10 transition-colors duration-200 text-[11px] font-semibold bg-[var(--bg-header)] ${getTextColor()}`;
    const tableCellClass = `px-2 py-1 text-[11px] transition-colors duration-200 border-b border-r border-gray-200 dark:border-gray-700 ${getTextColor()}`;
    const searchBoxClass = `relative flex items-center rounded-md transition-all duration-300
        bg-[var(--input-bg)] border border-[var(--input-border)]
        ${isSearchExpanded ? 'w-52' : 'w-auto'}`;
    const searchInputClass = `w-full outline-none text-[11px] py-1 px-1 bg-transparent text-[var(--input-text)] placeholder:text-[var(--text-muted)]`;
    const datePickerClass = `outline-none w-16 text-[10px] cursor-pointer bg-transparent text-[var(--input-text)]`;

    return (
        <div className={containerClass}>
            <div className={cardClass}>
                {/* Header */}
                <div className="flex-shrink-0 px-2 py-1 border-b border-[var(--border-color)]">
                    <div className="flex items-center justify-between gap-2">
                        {/* Search */}
                        <div className="flex items-center gap-1">
                            <div ref={searchContainerRef} className={searchBoxClass}>
                                <button
                                    onClick={handleSearchExpand}
                                    className={`p-1 rounded-l-md transition-all duration-200
                                        ${isSearchExpanded 
                                            ? 'bg-blue-500 text-white'
                                            : 'hover:bg-[var(--bg-hover)] text-[var(--text-muted)]'
                                        }`}
                                >
                                    <FaSearch size={10} />
                                </button>
                                
                                <div className={`overflow-hidden transition-all duration-300 ${isSearchExpanded ? 'w-36' : 'w-0'}`}>
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        placeholder="Search..."
                                        className={searchInputClass}
                                        value={searchText}
                                        onChange={(e) => {
                                            setSearchText(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                </div>
                                
                                {isSearchExpanded && searchText && (
                                    <button
                                        onClick={clearSearch}
                                        className={`p-1 rounded-r-md transition-all duration-200 hover:bg-[var(--bg-hover)] text-[var(--text-muted)]`}
                                    >
                                        <FiX size={10} />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1">
                            <div className={`flex items-center rounded-md border px-1.5 py-0.5 text-[10px] border-[var(--border-color)] bg-[var(--input-bg)]`}>
                                <FaCalendarAlt className={`mr-1 text-[var(--text-muted)]`} size={9} />
                                <DatePicker
                                    selected={selectedDate}
                                    onChange={handleDateChange}
                                    dateFormat="dd/MM/yy"
                                    className={datePickerClass}
                                />
                            </div>

                            <button onClick={handleDownload} className={buttonClass} title="Download">
                                <FaDownload size={11} />
                            </button>
                            
                            <button onClick={handleRefresh} className={buttonClass} title="Refresh">
                                <FiRefreshCcw size={11} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Loading Indicator */}
                {isLoading && !isRefreshing && (
                    <div className="flex items-center justify-center py-3 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800">
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        <span className="ml-2 text-[11px] text-blue-600 dark:text-blue-400 font-medium">
                            Loading alerts for {selectedDate.toLocaleDateString()}...
                        </span>
                    </div>
                )}

                {/* Table */}
                <div className="flex-1 overflow-auto min-h-0">
                    <table className="w-full h-full">
                        <thead className={tableHeaderClass}>
                            <tr>
                                <th className="px-2 py-1 border-b text-left font-semibold w-28 sticky top-0 bg-inherit border-[var(--border-color)]">
                                    <div className="flex items-center text-gray-400 gap-0.5 cursor-pointer" onClick={() => handleSort('created')}>
                                        Alert Time
                                        <span className="text-blue-500">{getSortIcon('created')}</span>
                                    </div>
                                </th>
                                <th className="px-2 py-1 border-b text-left font-semibold w-20 sticky top-0 bg-inherit border-[var(--border-color)]">
                                    <div className="flex items-center text-gray-400 gap-0.5 cursor-pointer" onClick={() => handleSort('symbol')}>
                                        Symbol
                                        <span className="text-blue-500">{getSortIcon('symbol')}</span>
                                    </div>
                                </th>
                                <th className="px-2 py-1 border-b text-left font-semibold w-16 sticky top-0 bg-inherit border-[var(--border-color)]">
                                    <div className="flex items-center text-gray-400 gap-0.5 cursor-pointer" onClick={() => handleSort('exchange')}>
                                        Exchange
                                        <span className="text-blue-500">{getSortIcon('exchange')}</span>
                                    </div>
                                </th>
                                <th className="px-2 py-1 border-b text-left font-semibold w-16 sticky top-0 bg-inherit border-[var(--border-color)]">
                                    <div className="flex items-center text-gray-400 gap-0.5 cursor-pointer" onClick={() => handleSort('buy_sell')}>
                                        Buy/Sell
                                        <span className="text-blue-500">{getSortIcon('buy_sell')}</span>
                                    </div>
                                </th>
                                <th className="px-2 py-1 border-b text-left font-semibold w-20 sticky top-0 bg-inherit border-[var(--border-color)]">
                                    <div className="flex items-center text-gray-400 gap-0.5 cursor-pointer" onClick={() => handleSort('order_type')}>
                                        Order Type
                                        <span className="text-blue-500">{getSortIcon('order_type')}</span>
                                    </div>
                                </th>
                                <th className="px-2 py-1 border-b text-left font-semibold w-20 sticky top-0 bg-inherit border-[var(--border-color)]">
                                    <div className="flex items-center text-gray-400 gap-0.5 cursor-pointer" onClick={() => handleSort('status')}>
                                        Status
                                        <span className="text-blue-500">{getSortIcon('status')}</span>
                                    </div>
                                </th>
                                <th className="px-2 py-1 border-b text-left text-gray-400 font-semibold w-28 sticky top-0 bg-inherit border-[var(--border-color)]">
                                    Error Message
                                </th>
                                <th className="px-2 py-1 border-b text-left text-gray-400 font-semibold w-10 sticky top-0 bg-inherit border-[var(--border-color)]">
                                    Info
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                Array.from({ length: 5 }).map((_, idx) => (
                                    <tr key={`skeleton-${idx}`} className="animate-pulse">
                                        <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20"></div></td>
                                        <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16"></div></td>
                                        <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-12"></div></td>
                                        <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-10"></div></td>
                                        <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-14"></div></td>
                                        <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-12"></div></td>
                                        <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24"></div></td>
                                        <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-6"></div></td>
                                    </tr>
                                ))
                            ) : currentRows.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="h-32 text-center">
                                        <div className="flex flex-col items-center justify-center py-8">
                                            <div className="text-3xl text-[var(--text-muted)]">📭</div>
                                            <p className="text-xs text-gray-400 font-medium mt-2 text-[var(--text-secondary)]">
                                                No alerts found
                                            </p>
                                            <p className="text-[10px] mt-1 text-[var(--text-light)]">
                                                {searchText ? 'Try adjusting your search' : `No alerts for ${selectedDate.toLocaleDateString()}`}
                                            </p>
                                            <button 
                                                onClick={handleRefresh}
                                                className="mt-3 px-3 py-1 text-[10px] rounded-md bg-blue-500 text-white hover:bg-blue-600"
                                            >
                                                Refresh
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                currentRows.map((row, idx) => (
                                    <tr key={row.id || idx} className="transition-all duration-200 hover:bg-white/5 dark:hover:bg-white/5">
                                        <td className={tableCellClass}>
                                            <div className="text-[10px] font-mono">
                                                {formatDateTime(row.created)}
                                            </div>
                                        </td>
                                        <td className={`${tableCellClass} font-medium`}>
                                            {row.symbol}
                                        </td>
                                        <td className={tableCellClass}>
                                            {row.exchange}
                                        </td>
                                        <td className={tableCellClass}>
                                            <span className={`px-1 py-0.5 rounded text-[9px] font-semibold
                                                ${row.buy_sell === 'BUY' 
                                                    ? 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30'
                                                    : row.buy_sell === 'SELL'
                                                    ? 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30'
                                                    : 'text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800'
                                                }`}>
                                                {row.buy_sell === 'BUY' ? 'BUY' : row.buy_sell === 'SELL' ? 'SELL' : row.buy_sell}
                                            </span>
                                        </td>
                                        <td className={tableCellClass}>
                                            <span className={`px-1 py-0.5 rounded text-[9px] font-medium ${getStatusColor(row.status)}`}>
                                                {row.order_type === 'MARKET' ? 'MARKET' : row.order_type === 'LIMIT' ? 'LIMIT' : row.order_type}
                                            </span>
                                        </td>
                                        <td className={tableCellClass}>
                                            <div className="flex items-center gap-0.5">
                                                {getStatusIcon(row.status)}
                                                <span className={`px-1 py-0.5 rounded text-[9px] font-medium ${getStatusColor(row.status)}`}>
                                                    {row.status === 'SUCCESS' ? 'SUCCESS' : row.status === 'PENDING' ? 'PENDING' : row.status === 'FAILED' ? 'FAILED' : row.status}
                                                </span>
                                            </div>
                                        </td>
                                        <td className={tableCellClass}>
                                            <div className="max-w-28 truncate text-[10px] text-gray-400" title={row.error_msg}>
                                                {row.error_msg || '-'}
                                            </div>
                                        </td>
                                        <td className={tableCellClass}>
                                            <button 
                                                onClick={() => handleShowDetails(row)} 
                                                className="p-0.5 rounded transition-all duration-200 hover:bg-[var(--bg-hover)] text-gray-400"
                                                title="View Details"
                                            >
                                                <BsFillInfoCircleFill size={10} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {!isLoading && totalPages > 1 && (
                    <div className="flex-shrink-0 flex justify-between items-center px-2 py-1 border-t text-[10px] border-[var(--border-color)]">
                        <div className="text-[10px] text-gray-400">
                            {startIdx + 1}-{Math.min(startIdx + rowsPerPage, filteredData.length)} of {filteredData.length}
                        </div>
                        <div className="flex gap-0.5">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className={`px-1 py-0.5 text-[10px] rounded transition-all
                                    ${currentPage === 1 
                                        ? 'text-gray-500 cursor-not-allowed'
                                        : 'hover:bg-[var(--bg-hover)] text-gray-400'
                                    }`}
                            >
                                ←
                            </button>
                            
                            {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 3) {
                                    pageNum = i + 1;
                                } else if (currentPage <= 2) {
                                    pageNum = i + 1;
                                } else if (currentPage >= totalPages - 1) {
                                    pageNum = totalPages - 2 + i;
                                } else {
                                    pageNum = currentPage - 1 + i;
                                }
                                
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => setCurrentPage(pageNum)}
                                        className={`w-5 h-4 text-[10px] rounded transition-all
                                            ${currentPage === pageNum
                                                ? 'bg-blue-500 text-white'
                                                : 'hover:bg-[var(--bg-hover)] text-gray-400'
                                            }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                            
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                className={`px-1 py-0.5 text-[10px] rounded transition-all
                                    ${currentPage === totalPages 
                                        ? 'text-gray-500 cursor-not-allowed'
                                        : 'hover:bg-[var(--bg-hover)] text-gray-400'
                                    }`}
                            >
                                →
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal */}
            <Modal show={showModal} onHide={handleCloseModal} centered size="sm">
                <Modal.Header closeButton className={`py-1.5 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100'}`}>
                    <Modal.Title className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-900'}`}>
                        Alert Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className={`p-2 ${darkMode ? 'bg-gray-900 text-gray-400' : 'bg-white text-gray-700'}`}>
                    {selectedAlert && (
                        <div className="space-y-1.5 text-[11px]">
                            <div className="grid grid-cols-2 gap-1.5">
                                <div>
                                    <label className="text-[9px] font-semibold text-gray-500">Alert Time</label>
                                    <p className="text-[10px]">{formatDateTime(selectedAlert.created)}</p>
                                </div>
                                <div>
                                    <label className="text-[9px] font-semibold text-gray-500">Symbol</label>
                                    <p className="text-[10px] font-semibold">{selectedAlert.symbol}</p>
                                </div>
                                <div>
                                    <label className="text-[9px] font-semibold text-gray-500">Exchange</label>
                                    <p className="text-[10px]">{selectedAlert.exchange}</p>
                                </div>
                                <div>
                                    <label className="text-[9px] font-semibold text-gray-500">Buy/Sell</label>
                                    <p className={`text-[10px] font-semibold ${selectedAlert.buy_sell === 'BUY' ? 'text-green-600' : 'text-red-600'}`}>
                                        {selectedAlert.buy_sell}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-[9px] font-semibold text-gray-500">Order Type</label>
                                    <p className="text-[10px]">{selectedAlert.order_type}</p>
                                </div>
                                <div>
                                    <label className="text-[9px] font-semibold text-gray-500">Status</label>
                                    <p className="text-[10px]">{selectedAlert.status}</p>
                                </div>
                                <div className="col-span-2">
                                    <label className="text-[9px] font-semibold text-gray-500">Error Message</label>
                                    <p className="text-[10px] text-gray-400">{selectedAlert.error_msg || 'No error'}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className={`py-1.5 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                    <Button size="sm" variant="secondary" onClick={handleCloseModal} className="text-[10px] py-1">
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default AlertTableView;